import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requiredRole?: 'user' | 'admin' | 'collector';
  fallback?: React.ReactElement;
}

export function ProtectedRoute({ 
  children, 
  requiredRole = 'user',
  fallback 
}: ProtectedRouteProps): React.ReactElement {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 로딩 중일 때
  if (isLoading) {
    return (
      <div className="loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  // 인증되지 않은 경우 로그인 페이지로 리다이렉션
  if (!isAuthenticated) {
    return <Navigate to="/sslmo/login" state={{ from: location }} replace />;
  }

  // 역할 기반 접근 제어
  if (requiredRole === 'admin' && user?.role !== 'admin') {
    return (
      <div className="forbidden-container">
        <h2>접근이 금지되었습니다</h2>
        <p>관리자 권한이 필요한 페이지입니다.</p>
        <button onClick={() => window.history.back()}>돌아가기</button>
      </div>
    );
  }

  // 수거원 전용 페이지 접근 제어
  if (requiredRole === 'collector' && user?.role !== 'collector') {
    return (
      <div className="forbidden-container">
        <h2>접근이 금지되었습니다</h2>
        <p>수거원 권한이 필요한 페이지입니다.</p>
        <button onClick={() => window.history.back()}>돌아가기</button>
      </div>
    );
  }

  // 모든 조건을 만족하는 경우 자식 컴포넌트 렌더링
  return <>{children}</>;
}

// 관리자 전용 라우트 컴포넌트
export function AdminRoute({ children, fallback }: Omit<ProtectedRouteProps, 'requiredRole'>): React.ReactElement {
  return (
    <ProtectedRoute requiredRole="admin" fallback={fallback}>
      {children}
    </ProtectedRoute>
  );
}